import React, { useState, useEffect } from 'react';
import { Users, Plus, RefreshCw, AlertCircle, Search } from 'lucide-react';
import { useAuth } from '../utils/AuthContext';
import userService, { UserData } from '../services/userService';
import UserCard from './UserCard';
import CreateUserModal from './CreateUserModal';

const UserList: React.FC = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const loadUsers = async () => {
    if (!user?.id) return;

    try {
      setError(null);
      const response = await userService.listUsers(user.id);

      if (response.success && response.users) {
        setUsers(response.users);
      } else {
        setError(response.message || 'Failed to load users');
      }
    } catch (error) {
      console.error('Error loading users:', error);
      setError('An unexpected error occurred while loading users');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadUsers();
    setRefreshing(false);
  };

  const handleToggleUserStatus = async (userId: string) => {
    if (!user?.id) return;

    try {
      const response = await userService.toggleUserStatus(user.id, userId);

      if (response.success) {
        // Refresh the users list to get updated status
        await loadUsers();
      } else {
        setError(response.message || 'Failed to update user status');
      }
    } catch (error) {
      console.error('Error toggling user status:', error);
      setError('An unexpected error occurred while updating user status');
    }
  };

  const handleUserCreated = () => {
    loadUsers(); // Refresh the list after creating a user
  };

  const handlePasswordReset = () => {
    // Optionally refresh the list or show a success message
    // For now, we don't need to do anything as the password reset doesn't change the user list
  };

  useEffect(() => {
    loadUsers();
  }, [user?.id]);

  // Filter users based on search term
  const filteredUsers = users.filter(userData =>
    userData.phone.toLowerCase().includes(searchTerm.toLowerCase()) ||
    userData.user_type.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Separate admins and users for better organization
  const adminUsers = filteredUsers.filter(u => u.user_type === 'admin');
  const regularUsers = filteredUsers.filter(u => u.user_type === 'user');

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary-600 border-t-transparent"></div>
          <span className="text-gray-600">Loading users...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center">
            <Users className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">User Management</h2>
            <p className="text-sm text-gray-600">Manage system users and their access</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          <button
            onClick={handleRefresh}
            className="flex items-center space-x-2 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
          
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center space-x-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Create User</span>
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search users by phone or role..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
      </div>

      {/* Error Message */}
      {error && (
        <div className="flex items-center space-x-2 p-4 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-600 hover:text-red-700"
          >
            <span className="sr-only">Dismiss</span>
            ×
          </button>
        </div>
      )}

      {/* Users List */}
      {filteredUsers.length === 0 ? (
        <div className="text-center py-8">
          <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchTerm ? 'No matching users found' : 'No users found'}
          </h3>
          <p className="text-gray-600 mb-4">
            {searchTerm 
              ? `No users match "${searchTerm}". Try adjusting your search.`
              : 'Get started by creating your first user.'
            }
          </p>
          {!searchTerm && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary"
            >
              <Plus className="w-4 h-4" />
              Create First User
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {/* Admin Users */}
          {adminUsers.length > 0 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center space-x-2">
                <span>Administrators</span>
                <span className="bg-purple-100 text-purple-700 text-xs px-2 py-1 rounded-full">
                  {adminUsers.length}
                </span>
              </h3>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {adminUsers.map((userData) => (
                  <UserCard
                    key={`${userData.id}-${userData.is_active}`}
                    user={userData}
                    currentUserId={user?.id || ''}
                    onToggleStatus={handleToggleUserStatus}
                    onPasswordReset={handlePasswordReset}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Regular Users */}
          {regularUsers.length > 0 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center space-x-2">
                <span>Users</span>
                <span className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full">
                  {regularUsers.length}
                </span>
              </h3>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {regularUsers.map((userData) => (
                  <UserCard
                    key={`${userData.id}-${userData.is_active}`}
                    user={userData}
                    currentUserId={user?.id || ''}
                    onToggleStatus={handleToggleUserStatus}
                    onPasswordReset={handlePasswordReset}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Statistics */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-medium text-gray-900 mb-2">Statistics</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-gray-600">Total Users:</span>
            <span className="font-semibold text-gray-900 ml-2">{users.length}</span>
          </div>
          <div>
            <span className="text-gray-600">Admins:</span>
            <span className="font-semibold text-purple-600 ml-2">{adminUsers.length}</span>
          </div>
          <div>
            <span className="text-gray-600">Active:</span>
            <span className="font-semibold text-green-600 ml-2">
              {users.filter(u => u.is_active).length}
            </span>
          </div>
          <div>
            <span className="text-gray-600">Inactive:</span>
            <span className="font-semibold text-red-600 ml-2">
              {users.filter(u => !u.is_active).length}
            </span>
          </div>
        </div>
      </div>

      {/* Create User Modal */}
      <CreateUserModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onUserCreated={handleUserCreated}
      />
    </div>
  );
};

export default UserList;
